import RevertDefault from '../../blocks/revert-default/revert-default';
import SslSupportService from '../../blocks/ssl-support-service/ssl-support-service';
import alertService from '@jetbrains/ring-ui/components/alert-service/alert-service';

export default class UpgradeSettingsController {
  /*@ngInject*/
  constructor($q, $scope, $location, dialog, installerResource, fields, wizardInfo, refreshWizardInfo, urlHelper,
    startInstallation, i18n, baseUrlHelper, oldUpgradeManager, sslCertificateDialog) {
    this.$q = $q;
    this.$scope = $scope;
    this.$location = $location;
    this.dialog = dialog;
    this.fields = fields;
    this.wizardInfo = wizardInfo;
    this.installerResource = installerResource;
    this.startInstallation = startInstallation;
    this.validationMessages = baseUrlHelper.VALIDATION_MESSAGES;
    this.urlHelper = urlHelper;
    this.baseUrlHelper = baseUrlHelper;
    this.oldUpgradeManager = oldUpgradeManager;
    this.refreshWizardInfo = refreshWizardInfo;
    this.sslCertificateDialog = sslCertificateDialog;
    this.certificateUrl = null;

    this.checkPathResource = installerResource.checkUpgradeSettingsPath;
    this.upgradeSettings = installerResource.getSingletonOf('upgradeSettings');
    this.upgradeSettingsDefault = installerResource.upgradeSettingsDefault.get();
    this.upgradeSettingsCandidate = installerResource.upgradeSettingsCandidate;
    this.sslSupportService = new SslSupportService();

    this.licenseDefaultForUpgrade = installerResource.licenseSettingsDefaultsForUpgrade.get();

    this.isSaving = true;
    this.verifySettingsOnServer(this.upgradeSettings).
      finally(() => {
        this.isSaving = false;
      });
    this.i18n = i18n;

    $q.all([this.upgradeSettings.$promise, this.upgradeSettingsDefault.$promise]).then(() => {
      this.revertDefault = new RevertDefault(this.upgradeSettings, this.upgradeSettingsDefault);
      this.isLoaded = true;
    }, () => {
      installerResource.clearSingletonOf('upgradeSettings');
    });
  }

  nextStep(validateUrl = true) {

    if (this.upgradeSettings[this.fields.UPGRADE_EXTERNAL_HUB_USED]) {
      this.upgradeSettings[this.fields.UPGRADE_EXTERNAL_HUB_URL] = this.urlHelper.addProtocolIfNotExist(this.upgradeSettings[this.fields.UPGRADE_EXTERNAL_HUB_URL]);
    }
    delete this.errors;
    this.isSaving = true;

    return this.$q.resolve(validateUrl ? this.baseUrlHelper.verifyBaseUrl(this.upgradeSettings) : null).
      catch(error => {
        this.verifySettingsOnServer(this.upgradeSettings).
          catch(err => {
            this.errors = Object.assign({}, this.errors, this.installerResource.extractErrorMessage(err.data || err));
          });

        return this.$q.reject(error);
      }).
      then(verificationResult => {
        this.upgradeSettings[this.fields.IS_BUNDLE_ACCESSIBLE] = !verificationResult || verificationResult.isBaseUrlValid !== false;
        this.upgradeSettings[this.fields.BASE_URL_BACKEND_ACCESS_STATE] = verificationResult ? verificationResult.baseUrlBackendAccessState : null;
        this.upgradeSettings[this.fields.TEST_ENDPOINT_STATE] = verificationResult ? verificationResult.testEndpointState : null;
      }).
      then(() => this.upgradeSettings.$save()).
      then(() => {
        if (this.showNextButton()) {
          this.$location.url('upgrade/license');
        } else {
          this.startInstallation().catch(err => {
            if (err) {
              alertService.error(this.installerResource.extractErrorMessage(err));
              return this.$q.reject(err);
            }
          });
        }
      }).
      catch(response => {
        if (!response) {
          return;
        }

        this.errors = this.installerResource.extractErrorMessage(response.data || response.error || response);

        const shouldRemoveOldData = this.errors.sourceLocation && this.errors.sourceLocation.error === 'OLD_UPGRADE_DATA_MUST_BE_REMOVED';
        const isBadHubSsl = response.status === 400 && response.data.additionalInfo && response.data.additionalInfo.untrustedCertificate;

        if (shouldRemoveOldData) {
          // User have loaded alternative upgrade source (different from that one detected by wizard on startup), so old configs should be backed up and removed.
          // (Use case: upgrade was detected by wizard => upgrade settings are shown -> then User follows the link 'Chose another Upgrade Source' -> then upgrade settings from another source were loaded and submitted)
          return this.oldUpgradeManager.manageExistUpgrade(false).
            then(() => this.nextStep(false));
        }

        if (isBadHubSsl) {
          return this.manageHubSslCertificate({data: response.data}).
            then(() => this.nextStep(false));
        }

        if (this.errors.sourceLocation) {
          this.errors.dataDir = this.errors.sourceLocation;
        }
      }).
      finally(() => {
        this.isSaving = false;
      });
  }

  verifySettingsOnServer(resource) {
    return resource.$promise.
      then(() => this.installerResource.upgradeSettingsCandidate.save(resource).$promise).
      then(res => Object.assign(this.upgradeSettings, res)).
      catch(response => {
        this.errors = this.installerResource.extractErrorMessage(response.data);
        const hubUrlError = this.errors[this.fields.UPGRADE_EXTERNAL_HUB_URL];
        if (hubUrlError && hubUrlError.error === 'UNTRUSTED_CERTIFICATE') {
          hubUrlError.isErrorWithExternalHubCertificate = true;
        }

        return this.$q.reject(this.errors);
      });
  }

  selectAnotherConfiguration() {
    this.wizardInfo.isUpgradeDetected = false;
    this.$location.path('upgrade/source');
  }

  discardAndInstall() {
    this.oldUpgradeManager.manageExistUpgrade(true).
      then(() => this.refreshWizardInfo()).
      then(() => this.$location.path('/install/base'));
  }

  isNextDisabled() {
    return this.isSaving || (!this.errors && this.$scope.upgradeSettingsForm.$invalid);
  }

  isUpgradeOnlyMode() {
    return this.wizardInfo.isUpgradeDetected && this.wizardInfo.isDetectedUpgradeSourceValid;
  }

  manageHubSslCertificate(error) {
    return this.sslCertificateDialog(error.data.additionalInfo).
      then(res => {
        if (this.errors && this.errors[this.fields.UPGRADE_EXTERNAL_HUB_URL]) {
          delete this.errors[this.fields.UPGRADE_EXTERNAL_HUB_URL];
        }
        return res;
      }).
      catch(() => this.$q.reject(error));
  }

  manageHubSslAndRevalidateSettings() {
    if (!this.errors || !this.errors.originalResponse || !this.errors.originalResponse.additionalInfo) {
      throw new Error('Installation Wizard: no information to manage Hub SSL certificate.');
    }
    const error = {
      data: {
        additionalInfo: this.errors.originalResponse.additionalInfo
      }
    };

    return this.manageHubSslCertificate(error).
      then(() => this.verifySettingsOnServer(this.upgradeSettings));
  }

  getErrorMessage(field) {
    if (this.errors) {
      const error = this.errors[field];
      return error ? error.error_description : error;
    }
    return null;
  }

  clearErrorMessage(field) {
    if (this.errors && this.errors[field]) {
      delete this.errors[field];
    }
  }

  checkPathAccess(path, property) {
    return this.checkPathResource.get({property, path}).$promise;
  }

  showNextButton() {
    return typeof this.licenseDefaultForUpgrade.key !== 'undefined';
  }

  onKeyPress($event) {
    if ($event.which === 13) {
      if (!this.isNextDisabled()) {
        this.nextStep();
      }
    }
  }

  getDifferentUpgradeSourceHint() {
    const minVersion = this.upgradeSettings[this.fields.UPGRADE_MINIMAL_COMPATIBLE];
    const minimalVersionString = minVersion ? ` (${minVersion} or later)` : '';

    return `If you have another backup of your ${this.wizardInfo.productName} database in different location,
     you can upgrade your ${this.wizardInfo.productName} installation with settings from this backup location.
     If you are upgrading from a previous ${this.wizardInfo.productName} installation ${minimalVersionString}, you can select
     the location of a previous installation as the upgrade source. The current settings are
     not discarded until you confirm the selection of a backup or the location of a previous installation.`;
  }

  getDiscardSettingsHint() {
    return 'If you want to perform a clean installation, you can discard these settings. You will be prompted to create a backup of the previous installation.';
  }

  getDirectoryHint(field, disabled) {
    if (field === this.fields.DATA_DIR && this.wizardInfo.installationType === 'JAR' && disabled) {
      if (this.upgradeSettings[this.fields.UPGRADE_TYPE] !== this.fields.UPGRADE_TYPE_INSTALLATION_DIR) {
        // Simple upgrade from backup (either from clean installation Wizard, or after detected upgrade source was changed to backup)
        return `${'For a JAR distribution, you cannot update this field in the Configuration Wizard. ' +
          'All data resolved from selected backup will be copied to this data directory. ' +
          'To change the location of the data directory, restart '}${ this.wizardInfo.productName
        } with the additional command line argument --data-dir=<path> and select your backup as an upgrade source in Configuration Wizard again. `;
      } else if (!this.wizardInfo.isUpgradeDetected || this.wizardInfo.installationDirectory !== this.upgradeSettings.sourceLocation) {
        // This is an upgrade from another existing installation.
        //  - either old installation directory wasn't detected (i.e., Upgrade optoin is chosen in clean installation wizard)
        //  - or detected upgrade source was changed to the installation directory of another instance
        return `${'For a JAR distribution, you cannot update this field in the Configuration Wizard. ' +
          'All data resolved from selected installation folder will be copied to this data directory. ' +
          'To change the location of the data directory (perhaps, in order to reuse data-directory resolved from existing installation), ' +
          'restart '}${ this.wizardInfo.productName } with the additional command line argument --data-dir=<path_to_target_data_directory>,  ` +
          'and select that installation folder as an upgrade source in Configuration Wizard again ';
      } else {
        // This is a simple upgrade of detected installation without change of upgrade source
        return `${'For a JAR distribution, you cannot update this field in the Configuration Wizard. ' +
          'To change the location of the data directory, stop '}${ this.wizardInfo.productName}${', copy or move content of data directory to a desired location ' +
          'and start '}${ this.wizardInfo.productName}${' with the additional command line argument --data-dir=<new_data_directory_location_here>. ' +
          'Please note, that clean installation of '}${ this.wizardInfo.productName}${' will be started if specified directory is empty.'}`;
      }
    }
  }

  revertToDefault() {
    this.certificateUrl = null;
    this.revertDefault.revertToDefault();
  }
}



// WEBPACK FOOTER //
// ./src/pages/upgrade-settings/upgrade-settings__controller.js